import cn from 'classnames';
import { useCallback, useEffect, useState } from 'react';

import { useLatestValueRef } from '@webapp/common/hooks/use-latest-value-ref';
import { BundleType } from '@webapp/common/resources/entities';

import type { BundleEntity } from '../../entities/bundle';
import {
    selectBundles,
    selectCurrentBundleIndex,
    selectScrollToQuestionId,
    selectShowNav,
} from '../../machine/selectors';
import { useStateSelector } from '../../machine/use-state-selector';
import { useStore } from '../../store';
import { QuestionsSection } from '../questions';

import { SurveyNavForBundle } from './nav';
import { TextSection } from './text-section';

import css from './survey.css';

const Bundle: FC<{
    bundle: BundleEntity | null;
    index: number;
    marginStyle: CSSProperties;
    twisterReadOnly: boolean;
    offset: number;
}> = ({ bundle, index, marginStyle, twisterReadOnly, offset }) => {
    const [rootRef, setRootRef] = useState<HTMLDivElement | null>(null);
    const [isChildMoreThan80percent, setIsChildMoreThan80percent] = useState(false);
    const { send } = useStore();
    const resetScroll = useCallback(() => send({ type: 'SCROLL_RESET' }), [send]);
    const showNav = useStateSelector(selectShowNav);
    const scrollToQuestionId = useStateSelector(selectScrollToQuestionId);

    useEffect(() => {
        if (!rootRef) return;
        const firstChild = rootRef.firstChild as HTMLElement;
        const recheckHeight = () => {
            const rootHeight = rootRef.clientHeight;
            const firstChild = rootRef.firstChild as HTMLElement;
            const childHeight = firstChild.clientHeight;
            setIsChildMoreThan80percent(0.8 * rootHeight <= childHeight);
        };
        recheckHeight();
        const ro = new ResizeObserver(recheckHeight);
        const ro2 = new ResizeObserver(recheckHeight);
        ro.observe(firstChild);
        ro2.observe(rootRef);
        return () => {
            ro.unobserve(firstChild);
            ro2.unobserve(rootRef);
        };
    }, [rootRef, index]);

    if (!bundle) {
        return null;
    }
    const { questions, text, type } = bundle;

    const render = () => {
        switch (type) {
            case BundleType.QUESTIONS: {
                return (
                    <QuestionsSection
                        twisterReadOnly={twisterReadOnly}
                        questions={questions}
                        resetScroll={resetScroll}
                        scrollToQuestionId={scrollToQuestionId}
                        offset={offset}
                    />
                );
            }
            case BundleType.TEXT:
                return <TextSection text={text} />;
        }
    };

    return (
        <div
            ref={setRootRef}
            style={marginStyle}
            className={cn(
                css.currentBundleNew,
                isChildMoreThan80percent && css.currentBundleNewNoCenter,
                'currentBundleNew',
                // withClickReply && showBundle && css.nextBundleShow,
            )}
        >
            {render()}
            {showNav && <SurveyNavForBundle bundleIndex={index} debounceTimeout={1_000} />}
        </div>
    );
};

export const BundlesForClickReply: FC<{ marginStyle: CSSProperties; hide?: boolean }> = ({ marginStyle, hide }) => {
    const [isSliding, setSliding] = useState(false);
    const bundles = useStateSelector(selectBundles);

    const currentBundleIndexRaw = useStateSelector(selectCurrentBundleIndex);
    const [offset, setOffset] = useState(currentBundleIndexRaw > 2 ? 2 : currentBundleIndexRaw);
    const currentBundleIndexRawRef = useLatestValueRef(currentBundleIndexRaw);
    const [currentBundleIndex, setCurrentBundleIndex] = useState(currentBundleIndexRaw);
    const currentBundleIndexRef = useLatestValueRef(currentBundleIndex);
    const startSlice = Math.max(currentBundleIndex - 2, 0);
    const slideUp = useCallback(() => {
        setOffset((prev) => prev + 1);
        setSliding(true);
        return setTimeout(() => {
            setSliding(false);
            setCurrentBundleIndex(currentBundleIndexRawRef.current);
            setOffset((prev) => {
                if (currentBundleIndexRawRef.current > 2) return 2;
                return prev;
            });
        }, 1000);
    }, []);

    const slideDown = useCallback(() => {
        setOffset((prev) => prev - 1);
        setSliding(true);
        return setTimeout(() => {
            setSliding(false);
            setCurrentBundleIndex(currentBundleIndexRawRef.current);
            setOffset(() => {
                if (currentBundleIndexRawRef.current < 2) return currentBundleIndexRawRef.current;
                return 2;
            });
        }, 1000);
    }, [bundles.length]);

    useEffect(() => {
        if (
            currentBundleIndexRaw > currentBundleIndexRef.current &&
            currentBundleIndexRaw - currentBundleIndexRef.current === 1
        ) {
            const timeoutId = slideUp();
            return () => window.clearTimeout(timeoutId);
        } else if (
            currentBundleIndexRaw < currentBundleIndexRef.current &&
            currentBundleIndexRef.current - currentBundleIndexRaw === 1
        ) {
            const timeoutId = slideDown();
            return () => window.clearTimeout(timeoutId);
        } else if (Math.abs(currentBundleIndexRef.current - currentBundleIndexRaw) > 1) {
            setCurrentBundleIndex(currentBundleIndexRaw);
            setOffset(currentBundleIndexRaw > 2 ? 2 : currentBundleIndexRaw);
        }
    }, [currentBundleIndexRaw]);

    useEffect(() => {
        if (hide) {
            setOffset((prev) => prev + 1);
            setSliding(true);
            const timeoutId = setTimeout(() => {
                setSliding(false);
            }, 1000);
            return () => window.clearTimeout(timeoutId);
        }
    }, [hide]);

    return (
        <div className={css.bundlesNewWrap}>
            <div
                className={cn(css.bundlesNew, { [css.bundlesNewSliding]: isSliding })}
                style={{
                    transition: isSliding ? '1s transform cubic-bezier(0.53, 0, 0, 1)' : undefined,
                    transform: `translateY(calc((100 * var(--vh, 1vh) - var(--survey-header-size, 0px) - var(--survey-footer-size, 0px)) * ${
                        offset * -1
                    }))`,
                }}
            >
                {bundles
                    .map((bundle, idx) => ({ bundle, index: idx }))
                    .slice(startSlice, startSlice + 5)
                    .map(({ bundle, index }) => (
                        <Bundle
                            twisterReadOnly={currentBundleIndexRaw !== index}
                            marginStyle={marginStyle}
                            key={index}
                            index={index}
                            bundle={bundle}
                            offset={currentBundleIndexRaw}
                        />
                    ))}
            </div>
        </div>
    );
};
